import { Preferences } from '@capacitor/preferences'

import { isDocked, isDockFollower, isNative } from './platform'

/**
 * Token storage abstraction.
 *
 * Native (patient flow): the eternal Sanctum bearer token issued by
 * `POST /api/patient/bootstrap`. Lives in `@capacitor/preferences`, which
 * is keychain-backed on iOS and EncryptedSharedPreferences on Android (as of
 * Capacitor 8). Survives app updates; lost only on uninstall.
 *
 * Web (therapist flow): the JWT minted by the upstream identity platform.
 * The integration is not yet built — for local development we read it
 * from `import.meta.env.VITE_DEV_THERAPIST_JWT` if present, otherwise from
 * `localStorage.therapist_jwt`. Production wiring will replace this.
 *
 * The patient's side of a Session App Dock holds no token at all (decision E1:
 * an unauthenticated follower, hydrated entirely over the peer channel). Asking
 * for one there throws rather than returning null, because a silent null reads
 * as "not signed in yet" and invites a retry loop against a backend the
 * follower must never call. See {@link FollowerHasNoSessionError}.
 *
 * A dock keeps its session in **sessionStorage**, not localStorage, because
 * localStorage is shared by every tab on the origin and a dock session must not
 * be. Two consequences, both load-bearing:
 *
 *   - the follower's tab cannot read a leader's token just because the two
 *     happen to share a browser (they do in the e2e harness, and they would on
 *     a therapist's machine with the portal embed open in another tab);
 *   - a dock and a portal embed side by side no longer overwrite each other's
 *     session, which they did while both wrote the same localStorage key.
 *
 * It also matches the lifetime: a dock session is scoped to one consultation
 * and should not outlive the tab.
 */

const PATIENT_TOKEN_KEY = 'patient_token'
const THERAPIST_TOKEN_KEY = 'therapist_jwt'

/** Thrown when the docked patient's side is asked for a credential it cannot have. */
export class FollowerHasNoSessionError extends Error {
  constructor() {
    super('The docked follower holds no session; this call should not have been made.')
    this.name = 'FollowerHasNoSessionError'
  }
}

export async function getPatientToken(): Promise<string | null> {
  if (!isNative) return null
  const { value } = await Preferences.get({ key: PATIENT_TOKEN_KEY })
  return value
}

export async function setPatientToken(token: string): Promise<void> {
  if (!isNative) return
  await Preferences.set({ key: PATIENT_TOKEN_KEY, value: token })
}

export async function clearPatientToken(): Promise<void> {
  if (!isNative) return
  await Preferences.remove({ key: PATIENT_TOKEN_KEY })
}

/** localStorage for the portal embed, sessionStorage for a dock — see above. */
function therapistStore(): Storage | null {
  try {
    const store = isDocked ? globalThis.sessionStorage : globalThis.localStorage
    return store ?? null
  } catch {
    return null // storage unavailable (private mode / SSR)
  }
}

export function getTherapistToken(): string | null {
  if (isDockFollower()) throw new FollowerHasNoSessionError()
  // The dev token is a whole-caseload therapist session with no patient scope.
  // In a dock that would quietly defeat the scoping the dock exists to prove —
  // and only locally, which is the worst place for the difference to hide. The
  // dock always uses the session it was handed.
  if (!isDocked && import.meta.env.VITE_DEV_THERAPIST_JWT) {
    // TODO: replace with the real upstream-platform JWT handoff.
    return import.meta.env.VITE_DEV_THERAPIST_JWT
  }
  return therapistStore()?.getItem(THERAPIST_TOKEN_KEY) ?? null
}

export function setTherapistToken(jwt: string): void {
  if (isDockFollower()) throw new FollowerHasNoSessionError()
  therapistStore()?.setItem(THERAPIST_TOKEN_KEY, jwt)
}

export function clearTherapistToken(): void {
  therapistStore()?.removeItem(THERAPIST_TOKEN_KEY)
}
